import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { debounceTime, forkJoin, fromEvent, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommunityService } from 'src/app/services/community.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
@Component({
  selector: 'app-edit-community',
  templateUrl: './edit-community.component.html',
  styleUrls: ['./edit-community.component.scss'],
})
export class EditCommunityComponent implements OnInit, AfterViewInit {
  communityDetails: any = {};
  memberDetails: any = {};
  selectedItems = [];
  communityId: any;
  isPage = false;
  searchText = '';
  memberIds: any = [];
  userNameSearch = '';
  userList = [];
  users: any;

  adminList: any;

  // Details card (name, description, location, logo, cover)
  logoFile: File | null = null;
  coverFile: File | null = null;
  logoPreview = '';
  coverPreview = '';
  private removedImages = { logo: false, cover: false };
  isSavingDetails = false;

  get isTemple(): boolean {
    return this.communityDetails?.pageType === 'community';
  }
  get itemLabel(): string {
    const type = this.communityDetails?.pageType;
    return type === 'festival' ? 'Festival' : type === 'community' ? 'Temple' : 'Page';
  }

  userForm = new FormGroup({
    FirstName: new FormControl(''),
    LastName: new FormControl(''),
    Country: new FormControl('', Validators.required),
    Zip: new FormControl('', Validators.required),
    MobileNo: new FormControl(''),
    City: new FormControl(''),
    State: new FormControl(''),
    Email: new FormControl('', Validators.required),
    Username: new FormControl('', Validators.required),
    UserID: new FormControl('', Validators.required),
    ProfilePicName: new FormControl('', Validators.required),
    CoverPicName: new FormControl('', Validators.required)
  });
  allCountryData: any;
  @ViewChild('zipCode') zipCode: ElementRef;
  isEdit = false;

  constructor(
    private communityService: CommunityService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    public toastService: ToastService
  ) {
    this.communityId = this.route.snapshot.paramMap.get('id');
    this.isPage = this.router.routerState.snapshot.url.includes('pages');
  }

  ngOnInit(): void {
    this.getUserDetails();
    this.getAllCountries();
  }
  ngAfterViewInit(): void {
    this.getUserList();
    fromEvent(this.zipCode.nativeElement, 'input')
      .pipe(debounceTime(1000))
      .subscribe((event) => {
        const val = event['target'].value;
        if (val.length > 3) {
          this.onZipChange(val);
        }
      });
  }

  getUserDetails(): void {
    this.spinner.show();
    this.communityService.getCommunityById(this.communityId).subscribe({
      next: (res: any) => {
        if (res) {
          this.spinner.hide();
          this.communityDetails = res[0];
          const memberList = res[0].memberList || [];
          // Imported temples/festivals have no members yet, so the owner may not be in the list.
          this.memberDetails =
            memberList.find((ele: any) => ele.profileId === this.communityDetails.profileId) || {};
          this.memberIds = memberList.map((member) => member.profileId);
          this.adminList = [...memberList];
          const data = {
            FirstName: this.memberDetails.FirstName,
            LastName: this.memberDetails.LastName,
            Country: this.memberDetails.Country,
            Zip: this.memberDetails.Zip,
            City: this.memberDetails.City,
            State: this.memberDetails.State,
            Username: this.memberDetails.Username,
            Email: this.memberDetails.Email,
            MobileNo: this.memberDetails.MobileNo,
            UserID: this.memberDetails.UserID,
            ProfilePicName: this.memberDetails.ProfilePicName,
            CoverPicName: this.memberDetails?.CoverPicName,
          };
          Object.keys(data).forEach((key) => (data[key] = data[key] ?? ''));
          this.userForm.setValue(data);
        }
      },
      error: (error) => {
        this.spinner.hide();
        console.log(error);
      },
    });
  }

  onItemSelect(event) {
    this.getUserList(event.term);
    this.isEdit = true;
  }

  saveChanges(): void {
    console.log(this.communityDetails);
    if (this.userForm.value) {
      this.userService
        .updateProfile(this.memberDetails.profileId, this.userForm.value)
        .subscribe({
          next: (res: any) => {
            this.communityService
              .editCommunity(
                this.communityDetails.Id,
                {
                  notificationEmail: this.communityDetails.notificationEmail,
                }
              )
              .subscribe({
                next: (res: any) => {
                  this.spinner.hide();
                  this.isEdit = false;
                  this.toastService.success('Update successfully');
                },
                error: (error) => {
                  this.spinner.hide();
                  this.toastService.danger('Please try again');
                  console.log(error);
                },
              });
          },
          error: (error) => {
            this.spinner.hide();
            this.toastService.danger('Please try again');
            console.log(error);
          },
        });
      if (this.selectedItems.length) {
        this.selectedItems.forEach((e) => {
          this.createAdmin(e);
        });
      }
    }
  }

  onImageSelected(event: Event, kind: 'logo' | 'cover'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toastService.danger('Please choose an image file.');
      return;
    }
    const preview = URL.createObjectURL(file);
    if (kind === 'logo') {
      this.logoFile = file;
      this.logoPreview = preview;
    } else {
      this.coverFile = file;
      this.coverPreview = preview;
    }
    this.removedImages[kind] = false;
  }

  removeImage(kind: 'logo' | 'cover'): void {
    if (kind === 'logo') {
      this.logoFile = null;
      this.logoPreview = '';
      this.communityDetails.logoImg = null;
    } else {
      this.coverFile = null;
      this.coverPreview = '';
      this.communityDetails.coverImg = null;
    }
    this.removedImages[kind] = true;
  }

  saveDetails(): void {
    const name = (this.communityDetails.CommunityName || '').trim();
    if (!name) {
      this.toastService.danger(`${this.itemLabel} name is required.`);
      return;
    }

    const upload = (file: File | null): Observable<string | null> =>
      file
        ? this.communityService.uploadImage(file).pipe(map((event: any) => event?.body?.url ?? null))
        : of(null);

    this.isSavingDetails = true;
    this.spinner.show();
    forkJoin({ logo: upload(this.logoFile), cover: upload(this.coverFile) }).subscribe({
      next: ({ logo, cover }) => {
        const details = this.communityDetails;
        const payload: any = {
          CommunityName: name,
          CommunityDescription: details.CommunityDescription ?? '',
        };
        if (this.isTemple) {
          Object.assign(payload, {
            CommunityTag: details.CommunityTag ?? '',
            Address: details.Address ?? '',
            City: details.City ?? '',
            County: details.County ?? '',
            State: details.State ?? '',
            Zip: details.Zip ?? '',
            Phone: details.Phone ?? '',
            Website: details.Website ?? '',
          });
        }
        if (logo) payload.logoImg = logo;
        else if (this.removedImages.logo) payload.logoImg = '';
        if (cover) payload.coverImg = cover;
        else if (this.removedImages.cover) payload.coverImg = '';

        this.communityService.editCommunity(details.Id, payload).subscribe({
          next: () => {
            this.spinner.hide();
            this.isSavingDetails = false;
            if (logo) details.logoImg = logo;
            if (cover) details.coverImg = cover;
            this.logoFile = this.coverFile = null;
            this.logoPreview = this.coverPreview = '';
            this.removedImages = { logo: false, cover: false };
            this.toastService.success(`${this.itemLabel} details saved.`);
          },
          error: (error) => {
            this.spinner.hide();
            this.isSavingDetails = false;
            this.toastService.danger(error?.error?.message || 'Could not save. Please try again.');
          },
        });
      },
      error: () => {
        this.spinner.hide();
        this.isSavingDetails = false;
        this.toastService.danger('Image upload failed. Please try again.');
      },
    });
  }

  createAdmin(profileId): void {
    const data = {
      profileId: profileId,
      communityId: Number(this.communityId),
      isActive: 'Y',
      isAdmin: 'Y',
    };
    this.communityService.createCommunityAdminByMA(data).subscribe({
      next: (res: any) => {
        if (this.isPage) {
          this.router.navigate(['/pages']);
        } else if (this.communityDetails?.pageType === 'festival') {
          this.router.navigate(['/festivals']);
        } else {
          this.router.navigate(['/community']);
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  getUserList(search: string = ''): void {
    this.spinner.show();
    this.userService.getProfileList(search).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        if (res?.data?.length > 0) {
          this.userList = res.data;
        } else {
          this.selectedItems = [];
          this.userList = [];
        }
      },
      error: (error) => {
        this.spinner.hide();
        console.log(error);
      },
    });
  }

  getAllCountries() {
    this.spinner.show();
    this.userService.getCountriesData().subscribe({
      next: (result) => {
        this.spinner.hide();
        this.allCountryData = result;
      },
      error: (error) => {
        this.spinner.hide();
        console.log(error);
      },
    });
  }

  changeCountry(e) {
    this.userForm.get('Country').setValue(e.target.value);
    this.userForm.get('Zip').setValue('');
    this.userForm.get('State').setValue('');
    this.userForm.get('City').setValue('');
  }

  onZipChange(event) {
    this.spinner.show();
    const country = this.userForm.value.Country;
    this.userService.getZipData(event, country).subscribe({
      next: (data) => {
        this.spinner.hide();
        const zip_data = data[0];
        if (zip_data?.state) {
          zip_data ? this.userForm.get('State').setValue(zip_data.state) : null;
          zip_data ? this.userForm.get('City').setValue(zip_data.city) : null;
        } else {
          this.spinner.hide();
          // this.toastService.danger('Please check and enter valid country or zip code.');
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.log(err);
      },
    });
  }

  onChangeData(): void {
    this.isEdit = true;
  }

  onSelectUser(item): void {
    this.selectedItems.push(item.Id);
    console.log(item);
  }

  removeasAdmin(profileId) {
    this.communityService
      .removeFromCommunity(this.communityDetails?.Id, profileId)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.toastService.success(res.message);
            this.getUserDetails();
          }
        },
        error: (error) => {
          console.log(error);
          this.toastService.danger(error.message);
        },
      });
  }
}
